/**
 * React Hook for Visitor Tracking
 * Provides easy integration with React components
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  visitorTracker, 
  trackPageView, 
  trackEvent, 
  getVisitorData, 
  getPageViews,
  getTrackedEvents,
  clearTrackingData,
  type VisitorData,
  type PageView
} from '../lib/visitor-tracking';

export interface UseVisitorTrackingReturn {
  visitorData: VisitorData;
  pageViews: PageView[];
  trackedEvents: any[];
  trackEvent: (eventName: string, eventData?: any) => void;
  clearData: () => void;
  refreshData: () => void;
}

/**
 * Hook to integrate visitor tracking in React components
 */
export function useVisitorTracking(): UseVisitorTrackingReturn {
  const location = useLocation();

  // Track page views automatically when location changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Track page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackEvent('page_hidden', { page: location.pathname });
      } else if (document.visibilityState === 'visible') {
        trackEvent('page_visible', { page: location.pathname });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  // Track when user is about to leave the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackEvent('page_unload', { 
        page: location.pathname,
        sessionDuration: getVisitorData().sessionDuration
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]);

  const handleTrackEvent = useCallback((eventName: string, eventData?: any) => {
    trackEvent(eventName, eventData);
  }, []);

  const handleClearData = useCallback(() => {
    clearTrackingData();
  }, []);

  const refreshData = useCallback(() => {
    // Force a re-render by returning new objects
    return {
      visitorData: getVisitorData(),
      pageViews: getPageViews(),
      trackedEvents: getTrackedEvents()
    };
  }, []);

  return {
    visitorData: getVisitorData(),
    pageViews: getPageViews(),
    trackedEvents: getTrackedEvents(),
    trackEvent: handleTrackEvent,
    clearData: handleClearData,
    refreshData
  };
}

/**
 * Hook for tracking specific user interactions
 */
export function useInteractionTracking() {
  const { trackEvent } = useVisitorTracking();

  const trackClick = useCallback((elementName: string, additionalData?: any) => {
    trackEvent('click', { element: elementName, ...additionalData });
  }, [trackEvent]);

  const trackFormSubmit = useCallback((formName: string, additionalData?: any) => {
    trackEvent('form_submit', { form: formName, ...additionalData });
  }, [trackEvent]);

  const trackSearch = useCallback((query: string, results?: number) => {
    trackEvent('search', { query, results });
  }, [trackEvent]);

  const trackDownload = useCallback((fileName: string, fileType?: string) => {
    trackEvent('download', { fileName, fileType });
  }, [trackEvent]);

  const trackError = useCallback((errorType: string, errorMessage?: string) => {
    trackEvent('error', { errorType, errorMessage });
  }, [trackEvent]);

  const trackLogin = useCallback((method?: string) => {
    trackEvent('login', { method });
  }, [trackEvent]);

  const trackLogout = useCallback(() => {
    trackEvent('logout');
  }, [trackEvent]);

  const trackRegistration = useCallback((method?: string) => {
    trackEvent('registration', { method });
  }, [trackEvent]);

  return {
    trackClick,
    trackFormSubmit,
    trackSearch,
    trackDownload,
    trackError,
    trackLogin,
    trackLogout,
    trackRegistration
  };
}